import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '../contexts/CurrencyContext';
import api from '../utils/api';
import { hasPermission } from '../utils/permissions';

// Import product components
import ProductStats from '../components/products/ProductStats';
import ProductFilters from '../components/products/ProductFilters';
import ProductTable from '../components/products/ProductTable';
import SearchableSelect from '../components/ui/SearchableSelect';
import { formatDateDMY } from '../utils/dateFormat';
import DateInputDMY from '../components/DateInputDMY';
import FindDuplicatesButton from '../components/FindDuplicatesButton';

export default function ProductsEnhanced() {
  const navigate = useNavigate();
  const { formatAmount } = useCurrency();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSerialIndexes, setSelectedSerialIndexes] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Multi-filter state
  const [filterDivision, setFilterDivision] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStock, setFilterStock] = useState('all'); // 'all' | 'in_stock' | 'low_stock' | 'sold_out'
  
  const [formData, setFormData] = useState({
    name: '',
    part_number: '',
    category: 'others',  // Default to valid enum value
    sub_category: '',
    brand: '',
    division: '',
    description: '',
    price: '',
    model: '',
    specifications: '',
    supplier_warranty_period: '',
    purchase_date: '',
    installation_date: '',
    license_code: '',
    serial_numbers: [],
    opening_stock: 0,
    low_stock_threshold: 0
  });
  
  const [newSerial, setNewSerial] = useState('');
  const [showSerialModal, setShowSerialModal] = useState(false);
  const [serialFormData, setSerialFormData] = useState({
    serial_number: '',
    purchase_date: '',
    supplier_warranty_period: ''
  });
  const [bulkAssignData, setBulkAssignData] = useState({
    customer_id: '',
    sale_date: '',
    customer_warranty_period: '12',
    next_maintenance_date: '',
    license_code: ''
  });

  // Inline per-serial assign (from edit form)
  const [showInlineAssign, setShowInlineAssign] = useState(false);
  const [inlineAssignIndex, setInlineAssignIndex] = useState(null);
  const [inlineAssignData, setInlineAssignData] = useState({
    customer_id: '',
    sale_date: '',
    customer_warranty_period: '12',
    next_maintenance_date: '',
    license_code: ''
  });

  const openInlineAssign = (idx) => {
    setInlineAssignIndex(idx);
    setInlineAssignData({
      customer_id: '',
      sale_date: new Date().toISOString().slice(0, 10),
      customer_warranty_period: '12',
      next_maintenance_date: '',
      license_code: ''
    });
    setShowInlineAssign(true);
  };

  const applyInlineAssign = () => {
    if (!inlineAssignData.customer_id || !inlineAssignData.sale_date) {
      alert('Please select a customer and sale date');
      return;
    }
    const customer = customers.find((c) => c.id === inlineAssignData.customer_id);
    const saleDate = new Date(inlineAssignData.sale_date);
    const months = parseInt(inlineAssignData.customer_warranty_period) || 0;
    const endDate = new Date(saleDate);
    endDate.setMonth(endDate.getMonth() + months);

    const updated = [...formData.serial_numbers];
    updated[inlineAssignIndex] = {
      ...updated[inlineAssignIndex],
      status: 'sold',
      customer_id: inlineAssignData.customer_id,
      customer_name: customer?.name || '',
      sale_date: inlineAssignData.sale_date,
      customer_warranty_period: inlineAssignData.customer_warranty_period,
      customer_warranty_end_date: endDate.toISOString(),
      next_maintenance_date: inlineAssignData.next_maintenance_date || null,
      license_code: inlineAssignData.license_code || updated[inlineAssignIndex].license_code || null
    };
    setFormData({ ...formData, serial_numbers: updated });
    setShowInlineAssign(false);
    setInlineAssignIndex(null);
  };

  const unassignSerial = (idx) => {
    if (!window.confirm('Unassign this serial from the customer? Status will reset to In Stock.')) return;
    const updated = [...formData.serial_numbers];
    updated[idx] = {
      ...updated[idx],
      status: 'in_stock',
      customer_id: null,
      customer_name: null,
      sale_date: null,
      customer_warranty_period: null,
      customer_warranty_end_date: null
    };
    setFormData({ ...formData, serial_numbers: updated });
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsRes, customersRes, categoriesRes, subcategoriesRes, brandsRes, divisionsRes, modelsRes] = await Promise.all([
        api.get('/products'),
        api.get('/customers'),
        api.get('/master-data/categories'),
        api.get('/master-data/subcategories'),
        api.get('/master-data/brands'),
        api.get('/master-data/divisions'),
        api.get('/master-data/models')
      ]);
      
      setProducts(productsRes.data || []);
      setCustomers(customersRes.data || []);
      setCategories(categoriesRes.data || []);
      setSubcategories(subcategoriesRes.data || []);
      setBrands(brandsRes.data || []);
      setDivisions(divisionsRes.data || []);
      setModels(modelsRes.data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openSerialModal = () => {
    setSerialFormData({
      serial_number: '',
      purchase_date: '',
      supplier_warranty_period: ''
    });
    setShowSerialModal(true);
  };

  const addSerialNumber = () => {
    if (!serialFormData.serial_number.trim()) {
      alert('Please enter serial number');
      return;
    }
    if (!serialFormData.purchase_date) {
      alert('Please enter purchase date');
      return;
    }
    if (!serialFormData.supplier_warranty_period) {
      alert('Please enter supplier warranty period');
      return;
    }

    // Calculate supplier warranty expiry date
    const purchaseDate = new Date(serialFormData.purchase_date);
    const warrantyMonths = parseInt(serialFormData.supplier_warranty_period) || 0;
    const warrantyExpiry = new Date(purchaseDate);
    warrantyExpiry.setMonth(warrantyExpiry.getMonth() + warrantyMonths);

    setFormData({
      ...formData,
      serial_numbers: [
        ...formData.serial_numbers,
        {
          serial_number: serialFormData.serial_number,
          status: 'in_stock',
          purchase_date: serialFormData.purchase_date,
          supplier_warranty_period: warrantyMonths,
          supplier_warranty_expiry: warrantyExpiry.toISOString(),
          customer_id: null,
          customer_name: null,
          sale_date: null,
          customer_warranty_period: null,
          customer_warranty_end_date: null,
          next_maintenance_date: null
        }
      ]
    });
    
    setShowSerialModal(false);
    setSerialFormData({
      serial_number: '',
      purchase_date: '',
      supplier_warranty_period: ''
    });
  };

  const removeSerialNumber = (index) => {
    const updated = [...formData.serial_numbers];
    updated.splice(index, 1);
    setFormData({ ...formData, serial_numbers: updated });
  };

  const openBulkAssignModal = (product) => {
    setSelectedProduct(product);
    setSelectedSerialIndexes([]);
    
    // Fetch next maintenance date from activities for this product
    fetchNextMaintenanceFromActivities(product.id);
    
    setShowBulkAssignModal(true);
  };

  const fetchNextMaintenanceFromActivities = async (productId) => {
    try {
      const response = await api.get('/activities');
      const productActivities = response.data.filter(act => act.product_id === productId);
      
      // Get the most recent activity with next_maintenance_date
      const latestActivity = productActivities
        .filter(act => act.next_maintenance_date)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
      
      if (latestActivity && latestActivity.next_maintenance_date) {
        setBulkAssignData(prev => ({
          ...prev,
          next_maintenance_date: latestActivity.next_maintenance_date.split('T')[0]
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const toggleSerialSelection = (index) => {
    setSelectedSerialIndexes(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const bulkAssignToCustomer = async () => {
    if (!bulkAssignData.customer_id || !bulkAssignData.sale_date) {
      alert('Please select customer and sale date');
      return;
    }

    if (selectedSerialIndexes.length === 0) {
      alert('Please select at least one serial number');
      return;
    }

    try {
      const customer = customers.find(c => c.id === bulkAssignData.customer_id);
      const saleDate = new Date(bulkAssignData.sale_date);
      const warrantyMonths = parseInt(bulkAssignData.customer_warranty_period) || 0;
      const warrantyEndDate = new Date(saleDate);
      warrantyEndDate.setMonth(warrantyEndDate.getMonth() + warrantyMonths);

      const updatedSerials = [...selectedProduct.serial_numbers];
      
      // Update all selected serial numbers
      selectedSerialIndexes.forEach(index => {
        updatedSerials[index] = {
          ...updatedSerials[index],
          status: 'sold',
          customer_id: bulkAssignData.customer_id,
          customer_name: customer?.name || '',
          sale_date: bulkAssignData.sale_date,
          customer_warranty_period: warrantyMonths,
          customer_warranty_end_date: warrantyEndDate.toISOString(),
          next_maintenance_date: bulkAssignData.next_maintenance_date || null,
          license_code: bulkAssignData.license_code || null
        };
      });

      await api.put(`/products/${selectedProduct.id}`, {
        serial_numbers: updatedSerials
      });

      alert(`Successfully assigned ${selectedSerialIndexes.length} serial number(s) to ${customer?.name}!`);
      setShowBulkAssignModal(false);
      setBulkAssignData({
        customer_id: '',
        sale_date: '',
        customer_warranty_period: '12',
        next_maintenance_date: '',
        license_code: ''
      });
      setSelectedSerialIndexes([]);
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to assign serial numbers');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // DEBUG: Log complete form state
    
    try {
      // Clean up formData - convert empty strings to null for optional fields
      const cleanedData = {
        name: formData.name,
        part_number: formData.part_number || null,
        category: formData.category || null,  // Master data category (can be null)
        sub_category: formData.sub_category || null,
        brand: formData.brand || null,
        division: formData.division || null,
        description: formData.description || null,
        model: formData.model || null,
        specifications: formData.specifications || null,
        supplier_warranty_period: formData.supplier_warranty_period || null,
        license_code: formData.license_code || null,
        price: formData.price ? parseFloat(formData.price) : null,
        purchase_date: formData.purchase_date || null,
        installation_date: formData.installation_date || null,
        serial_numbers: formData.serial_numbers,
        opening_stock: parseFloat(formData.opening_stock) || 0,
        low_stock_threshold: parseFloat(formData.low_stock_threshold) || 0,
      };
      
      
      if (isEditMode && selectedProduct) {
        // Update existing product
        const res = await api.put(`/products/${selectedProduct.id}`, cleanedData);
        const c = res.data?._cascade;
        if (c && (c.sales_invoices || c.purchase_orders || c.purchase_entries)) {
          // Tell the user which downstream records were refreshed.
          alert(
            `Product updated successfully!\n\n` +
            `Linked records refreshed automatically:\n` +
            `  • Sales Invoices: ${c.sales_invoices || 0}\n` +
            `  • Purchase Orders: ${c.purchase_orders || 0}\n` +
            `  • Purchase Entries: ${c.purchase_entries || 0}`
          );
        } else {
          alert('Product updated successfully!');
        }
        
        // Reset form first
        resetForm();
        
        // Wait a bit then refresh data to ensure backend has processed
        setTimeout(() => {
          fetchData();
        }, 500);
      } else {
        // Create new product
        await api.post('/products', cleanedData);
        alert('Product created successfully!');
        
        resetForm();
        fetchData();
      }
    } catch (error) {
      console.error('Full error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Form data at error:', formData);
      const errorDetail = error.response?.data?.detail;
      let errorMsg = 'Unknown error';
      
      if (Array.isArray(errorDetail)) {
        // Pydantic validation errors
        errorMsg = errorDetail.map(err => `${err.loc.join('.')}: ${err.msg}`).join(', ');
      } else if (typeof errorDetail === 'string') {
        errorMsg = errorDetail;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      alert(isEditMode ? `Failed to update product: ${errorMsg}` : `Failed to create product: ${errorMsg}`);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setIsEditMode(false);
    setSelectedProduct(null);
    setFormData({
      name: '',
      part_number: '',
      category: '',  // Master data - no default
      sub_category: '',
      brand: '',
      division: '',
      description: '',
      price: '',
      model: '',
      specifications: '',
      supplier_warranty_period: '',
      purchase_date: '',
      installation_date: '',
      license_code: '',
      serial_numbers: [],
      opening_stock: 0,
      low_stock_threshold: 0
    });
  };

  const handleEditClick = (product) => {
    setSelectedProduct(product);
    setIsEditMode(true);
    setFormData({
      name: product.name || '',
      part_number: product.part_number || '',
      category: product.category || '',
      sub_category: product.sub_category || '',
      brand: product.brand || '',
      division: product.division || '',
      description: product.description || '',
      price: product.price || '',
      model: product.model || '',
      specifications: product.specifications || '',
      supplier_warranty_period: product.supplier_warranty_period || '',
      purchase_date: product.purchase_date ? product.purchase_date.split('T')[0] : '',
      installation_date: product.installation_date ? product.installation_date.split('T')[0] : '',
      license_code: product.license_code || '',
      serial_numbers: product.serial_numbers || [],
      opening_stock: product.opening_stock || 0,
      low_stock_threshold: product.low_stock_threshold || 0
    });
    setShowForm(true);
  };

  const handleViewDetails = (product) => {
    setSelectedProduct(product);
    setShowDetails(true);
  };

  // Admin-only: hard delete a product
  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`Delete product "${product.name}"? All serial numbers and history will be removed. This cannot be undone.`)) return;
    try {
      await api.delete(`/products/${product.id}`);
      alert('Product deleted successfully!');
      await fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error deleting product');
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/products/export/excel', {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T-]/g, '');
      link.href = url;
      link.setAttribute('download', `products_export_${ts}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      alert('Error exporting products');
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      alert('Please select an Excel (.xlsx) file');
      return;
    }

    try {
      const fd = new FormData();
      fd.append('file', csvFile);
      const response = await api.post('/products/import/excel', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert(`Import complete! Imported: ${response.data.imported}, Errors: ${response.data.errors.length}`);
      setShowImport(false);
      setCsvFile(null);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg;
      if (Array.isArray(detail)) {
        msg = detail.map(d => d?.msg || JSON.stringify(d)).join('; ');
      } else if (typeof detail === 'string') {
        msg = detail;
      } else if (detail) {
        msg = JSON.stringify(detail);
      } else {
        msg = error.message || 'Unknown error';
      }
      alert('Error importing products: ' + msg);
    }
  };

  // On-hand qty is the canonical stock figure now — driven by opening_stock
  // + Purchase Entries − Delivery Notes − Sales Invoices (see utils/stock_service).
  // Serial numbers remain a separate concept (warranty tracking) and no longer
  // determine the In-Stock count.
  const inStockCount = (product) => {
    return Number(product.on_hand_stock || 0);
  };

  const soldCount = (product) => {
    // Sold count = serial numbers marked sold (warranty trail). Independent of
    // on-hand qty.
    return product.serial_numbers?.filter(s => s.status === 'sold').length || 0;
  };

  const filteredProducts = products.filter(product => {
    if (filterDivision !== 'all' && (product.division || '') !== filterDivision) return false;
    if (filterCategory !== 'all' && (product.category || '') !== filterCategory) return false;
    if (filterStock !== 'all') {
      const inStock = Number(product.on_hand_stock || 0);
      const threshold = Number(product.low_stock_threshold || 0);
      if (filterStock === 'in_stock' && inStock <= 0) return false;
      if (filterStock === 'low_stock' && !(inStock > 0 && (threshold > 0 ? inStock <= threshold : inStock <= 3))) return false;
      if (filterStock === 'sold_out' && inStock > 0) return false;
    }
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      product.name?.toLowerCase().includes(query) ||
      product.part_number?.toLowerCase().includes(query) ||
      product.model?.toLowerCase().includes(query) ||
      product.category?.toLowerCase().includes(query) ||
      product.sub_category?.toLowerCase().includes(query) ||
      product.license_code?.toLowerCase().includes(query) ||
      product.serial_numbers?.some(s => s.serial_number?.toLowerCase().includes(query))
    );
  });

  const divisionOptions = Array.from(new Set(products.map(p => p.division).filter(Boolean))).sort();
  const categoryOptions = Array.from(new Set(products.map(p => p.category).filter(Boolean))).sort();

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="w-screen max-w-full overflow-x-hidden overflow-y-auto p-2 sm:p-4 lg:p-6">
      <div className="w-full max-w-full overflow-x-hidden">
        {/* Desktop only header */}
        {/* Desktop Header - Heading and Back Button on Same Line */}
        <div className="hidden lg:flex lg:items-center lg:justify-between mb-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
            📦 Products Master
          </h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Back to Dashboard
          </button>
        </div>

        {/* Stats Cards */}
        <ProductStats 
        products={products}
        inStockCount={inStockCount}
        soldCount={soldCount}
        formatAmount={formatAmount}
      />

      {/* Search and Action Buttons */}
      <ProductFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filteredProducts={filteredProducts}
        onExport={handleExport}
        onImport={() => setShowImport(true)}
        onAddProduct={() => setShowForm(true)}
        userRole={user.role}
        canCreate={hasPermission(user, 'products', 'create')}
      />

      {/* Duplicate Finder (admin only) */}
      <div className="mb-3">
        <FindDuplicatesButton
          module="products"
          labelFields={['name', 'part_number', 'brand', 'model', 'category', 'sub_category']}
          onAfterDelete={fetchData}
        />
      </div>

      {/* Extra filter row: division / category / stock state */}
      <div className="bg-white rounded-lg shadow p-3 mb-4 grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="products-advanced-filters">
        <select value={filterDivision} onChange={(e) => setFilterDivision(e.target.value)} className="px-3 py-2 border rounded text-sm" data-testid="products-filter-division">
          <option value="all">All divisions</option>
          {divisionOptions.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="px-3 py-2 border rounded text-sm" data-testid="products-filter-category">
          <option value="all">All categories</option>
          {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterStock} onChange={(e) => setFilterStock(e.target.value)} className="px-3 py-2 border rounded text-sm" data-testid="products-filter-stock">
          <option value="all">All stock states</option>
          <option value="in_stock">In stock</option>
          <option value="low_stock">Low stock (≤3)</option>
          <option value="sold_out">Sold out</option>
        </select>
        {(filterDivision !== 'all' || filterCategory !== 'all' || filterStock !== 'all') && (
          <button
            type="button"
            onClick={() => { setFilterDivision('all'); setFilterCategory('all'); setFilterStock('all'); }}
            className="px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
            data-testid="products-clear-filters-btn"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Products Table */}
      <ProductTable
        products={filteredProducts}
        searchQuery={searchQuery}
        inStockCount={inStockCount}
        soldCount={soldCount}
        formatAmount={formatAmount}
        onViewDetails={handleViewDetails}
        onEdit={handleEditClick}
        onDelete={handleDeleteProduct}
        userRole={user.role}
        canEdit={hasPermission(user, 'products', 'edit')}
        canDelete={hasPermission(user, 'products', 'delete')}
      />

      {/* Create/Edit Product Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-8 max-w-4xl w-full m-4 max-h-screen overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">
              {isEditMode ? 'Edit Product' : 'Add New Product'}
            </h2>
            
            <form onSubmit={handleSubmit}>
              {/* Basic Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4 border-b pb-2">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Product Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Part Number</label>
                    <input
                      type="text"
                      value={formData.part_number}
                      onChange={(e) => setFormData({...formData, part_number: e.target.value})}
                      className="w-full border rounded px-3 py-2"
                      placeholder="e.g., PN-12345"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Division</label>
                    <SearchableSelect
                      value={formData.division}
                      onChange={(val) => setFormData({
                        ...formData, 
                        division: val,
                        category: '',  // Clear category when division changes
                        sub_category: '',
                        brand: '',
                        model: ''
                      })}
                      options={divisions.map(div => ({ value: div.name, label: div.name }))}
                      placeholder="Select Division"
                    />
                    {formData.division && !divisions.find(d => d.name === formData.division) && (
                      <p className="text-xs text-orange-600 mt-1">
                        ⚠️ Current: "{formData.division}" (not in master data) - Please select a valid option
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Category</label>
                    <SearchableSelect
                      value={formData.category}
                      onChange={(val) => setFormData({
                        ...formData, 
                        category: val || '',
                        sub_category: '',
                        brand: '',
                        model: ''
                      })}
                      options={categories
                        .filter(cat => !formData.division || cat.parent_division === formData.division)
                        .map(cat => ({ value: cat.name, label: cat.name }))}
                      placeholder={formData.category || "Select Category (optional)"}
                    />
                    {formData.category && !categories.find(c => c.name === formData.category) && (
                      <p className="text-xs text-orange-600 mt-1">
                        ⚠️ Current: "{formData.category}" (not in master data) - Select a valid option
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Sub-Category</label>
                    <SearchableSelect
                      value={formData.sub_category}
                      onChange={(val) => setFormData({...formData, sub_category: val})}
                      options={subcategories
                        .filter(sub => sub.parent_category === formData.category)
                        .map(sub => ({ value: sub.name, label: sub.name }))}
                      placeholder={formData.sub_category || "Select Sub-Category"}
                      disabled={!formData.category}
                    />
                    {formData.sub_category && !subcategories.find(s => s.name === formData.sub_category) && (
                      <p className="text-xs text-orange-600 mt-1">
                        Current: "{formData.sub_category}" (not in master data) - Please select a valid option
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Brand</label>
                    <SearchableSelect
                      value={formData.brand}
                      onChange={(val) => setFormData({...formData, brand: val, model: ''})}
                      options={[
                        ...brands
                          .filter(brand => !formData.category || brand.parent_category === formData.category)
                          .map(brand => ({ value: brand.name, label: brand.name })),
                        // Add current value if not in list
                        ...(formData.brand && !brands.find(b => b.name === formData.brand) 
                          ? [{ value: formData.brand, label: formData.brand }] 
                          : [])
                      ]}
                      placeholder="Select Brand"
                      disabled={!formData.category}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Model</label>
                    <SearchableSelect
                      value={formData.model}
                      onChange={(val) => setFormData({...formData, model: val})}
                      options={models
                        .filter(model => !formData.brand || model.parent_brand === formData.brand)
                        .map(model => ({ value: model.name, label: model.name }))}
                      placeholder="Select Model"
                      disabled={!formData.brand}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Price</label>
                    <input
                      type="number"
                      step="0.001"
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: e.target.value})}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">License Code</label>
                    <input
                      type="text"
                      value={formData.license_code}
                      onChange={(e) => setFormData({...formData, license_code: e.target.value})}
                      placeholder="e.g., LIC-2024-XYZ"
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full border rounded px-3 py-2"
                      rows="3"
                    ></textarea>
                  </div>
                  
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Specifications</label>
                    <textarea
                      value={formData.specifications}
                      onChange={(e) => setFormData({...formData, specifications: e.target.value})}
                      placeholder="e.g., Power: 5kW, Pressure: 10 bar, Weight: 200kg"
                      className="w-full border rounded px-3 py-2"
                      rows="3"
                    ></textarea>
                  </div>
                </div>
              </div>

              {/* Warranty & Dates */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4 border-b pb-2">Warranty & Dates</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Supplier Warranty Period</label>
                    <input
                      type="text"
                      value={formData.supplier_warranty_period}
                      onChange={(e) => setFormData({...formData, supplier_warranty_period: e.target.value})}
                      placeholder="e.g., 12 months, 2 years"
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Purchase Date</label>
                    <DateInputDMY value={formData.purchase_date}
 onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
 className="w-full border rounded px-3 py-2"
 />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Installation Date</label>
                    <DateInputDMY value={formData.installation_date}
 onChange={(e) => setFormData({...formData, installation_date: e.target.value})}
 className="w-full border rounded px-3 py-2"
 />
                  </div>
                </div>
              </div>

              {/* Inventory / Stock */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4 border-b pb-2">Inventory</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Opening Stock {isEditMode && <span className="text-xs text-gray-500 font-normal">(read-only after creation — use Adjust Stock)</span>}
                    </label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      value={formData.opening_stock}
                      onChange={(e) => setFormData({ ...formData, opening_stock: e.target.value })}
                      readOnly={isEditMode}
                      className={`w-full border rounded px-3 py-2 ${isEditMode ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                      data-testid="product-opening-stock"
                    />
                    <p className="text-xs text-gray-500 mt-1">Starting on-hand qty. Auto-seeds the live count when you create the product.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Low-Stock Threshold</label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      value={formData.low_stock_threshold}
                      onChange={(e) => setFormData({ ...formData, low_stock_threshold: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      data-testid="product-low-stock-threshold"
                    />
                    <p className="text-xs text-gray-500 mt-1">When on-hand falls to or below this number, the product shows up in the low-stock chip.</p>
                  </div>
                  {isEditMode && selectedProduct && (
                    <div className="col-span-2 bg-blue-50 border border-blue-200 rounded p-3 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm text-gray-600">Live On-Hand Stock</div>
                        <div className="text-2xl font-bold text-blue-700" data-testid="product-on-hand-stock">
                          {Number(selectedProduct.on_hand_stock || 0).toLocaleString()}
                        </div>
                        {selectedProduct.last_received_at && (
                          <div className="text-xs text-gray-500 mt-1">Last received: {new Date(selectedProduct.last_received_at).toLocaleDateString()}</div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          const v = window.prompt('Adjust stock by (use negative to reduce):', '0');
                          if (v === null) return;
                          const delta = parseFloat(v);
                          if (!delta) return alert('Enter a non-zero number');
                          const note = window.prompt('Reason (optional):', '') || '';
                          try {
                            await api.post(`/products/${selectedProduct.id}/adjust-stock`, { qty_delta: delta, note });
                            const r = await api.get(`/products/${selectedProduct.id}`);
                            setSelectedProduct(r.data);
                            alert(`Stock updated. New on-hand = ${r.data.on_hand_stock}`);
                          } catch (err) {
                            alert(err.response?.data?.detail || 'Adjust failed');
                          }
                        }}
                        className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium px-3 py-2 rounded"
                        data-testid="product-adjust-stock-btn"
                      >
                        ✏️ Adjust Stock
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4 border-b pb-2">
                  Serial Numbers & Stock ({formData.serial_numbers.length} units)
                </h3>
                
                <button
                  type="button"
                  onClick={openSerialModal}
                  className="mb-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  + Add Serial Number
                </button>
                
                <div className="max-h-60 overflow-y-auto border rounded p-2">
                  {formData.serial_numbers.map((serial, idx) => (
                    <div key={serial.serial_number} className="flex flex-wrap justify-between items-center p-2 mb-2 bg-gray-50 rounded gap-2">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium">{serial.serial_number}</span>
                        <span className={`ml-3 px-2 py-1 text-xs rounded ${
                          serial.status === 'in_stock' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {serial.status === 'in_stock' ? 'In Stock' : 'Sold'}
                        </span>
                        {serial.purchase_date && (
                          <span className="ml-2 text-sm text-gray-600">
                            Purchased: {formatDateDMY(serial.purchase_date)}
                          </span>
                        )}
                        {serial.supplier_warranty_expiry && (
                          <span className="ml-2 text-sm text-orange-600">
                            Warranty Expires: {formatDateDMY(serial.supplier_warranty_expiry)}
                          </span>
                        )}
                        {serial.customer_name && (
                          <span className="ml-2 text-sm text-gray-600">
                            → {serial.customer_name}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {serial.status === 'in_stock' ? (
                          <button
                            type="button"
                            onClick={() => openInlineAssign(idx)}
                            className="text-green-700 hover:text-green-900 text-sm font-medium"
                            data-testid={`serial-assign-btn-${idx}`}
                          >
                            Assign to Customer
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => unassignSerial(idx)}
                            className="text-orange-600 hover:text-orange-800 text-sm font-medium"
                            data-testid={`serial-unassign-btn-${idx}`}
                          >
                            Unassign
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeSerialNumber(idx)}
                          className="text-red-600 hover:text-red-800 text-sm"
                          data-testid={`serial-remove-btn-${idx}`}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                  {formData.serial_numbers.length === 0 && (
                    <p className="text-gray-500 text-center py-4">No serial numbers added yet</p>
                  )}
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-blue-700 to-green-700 text-white rounded hover:shadow-lg"
                >
                  {isEditMode ? 'Update Product' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Product Details Modal */}
      {showDetails && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{selectedProduct.name}</h2>
                <p className="text-sm text-gray-500">
                  {selectedProduct.category} {selectedProduct.sub_category && `- ${selectedProduct.sub_category}`}
                </p>
              </div>
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-800 mb-3 text-lg">📋 Basic Information</h3>
                <div className="space-y-2 text-sm">
                  <div><span className="font-medium">Model:</span> {selectedProduct.model || '-'}</div>
                  <div><span className="font-medium">Price:</span> {formatAmount(selectedProduct.price || 0)}</div>
                  <div><span className="font-medium">License Code:</span> <span className="font-mono bg-white px-2 py-1 rounded">{selectedProduct.license_code || '-'}</span></div>
                </div>
              </div>

              {/* Warranty Information */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-green-800 mb-3 text-lg">⚠️ Warranty</h3>
                <div className="space-y-2 text-sm">
                  <div><span className="font-medium">Supplier Warranty:</span> {selectedProduct.supplier_warranty_period || '-'}</div>
                  <div><span className="font-medium">Purchase Date:</span> {selectedProduct.purchase_date ? formatDateDMY(selectedProduct.purchase_date) : '-'}</div>
                  <div><span className="font-medium">Installation Date:</span> {selectedProduct.installation_date ? formatDateDMY(selectedProduct.installation_date) : '-'}</div>
                </div>
              </div>

              {/* Stock Information */}
              <div className="bg-purple-50 p-4 rounded-lg col-span-2">
                <h3 className="font-semibold text-purple-800 mb-3 text-lg">📦 Serial Numbers & Stock</h3>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-green-100 p-2 rounded text-center">
                    <p className="text-sm text-gray-600">In Stock</p>
                    <p className="text-2xl font-bold text-green-700">{inStockCount(selectedProduct)}</p>
                  </div>
                  <div className="bg-gray-100 p-2 rounded text-center">
                    <p className="text-sm text-gray-600">Sold</p>
                    <p className="text-2xl font-bold text-gray-700">{soldCount(selectedProduct)}</p>
                  </div>
                </div>
                
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {selectedProduct.serial_numbers?.map((serial) => (
                    <div key={serial.serial_number} className="bg-white p-3 rounded border">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-mono font-medium">{serial.serial_number}</p>
                          {serial.license_code && (
                            <p className="text-sm text-purple-600 mt-1">
                              License: <span className="font-mono">{serial.license_code}</span>
                            </p>
                          )}
                          {serial.customer_name && (
                            <div className="text-sm text-gray-600 mt-1">
                              <p>Customer: {serial.customer_name}</p>
                              {serial.sale_date && <p>Sale Date: {formatDateDMY(serial.sale_date)}</p>}
                              {serial.customer_warranty_period && (
                                <p>Warranty: {serial.customer_warranty_period} months (ends {formatDateDMY(serial.customer_warranty_end_date)})</p>
                              )}
                              {serial.next_maintenance_date && (
                                <p>Next Maintenance: {formatDateDMY(serial.next_maintenance_date)}</p>
                              )}
                            </div>
                          )}
                        </div>
                        <span className={`px-2 py-1 text-xs rounded ${
                          serial.status === 'in_stock' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {serial.status === 'in_stock' ? 'In Stock' : 'Sold'}
                        </span>
                      </div>
                    </div>
                  ))}
                  {(!selectedProduct.serial_numbers || selectedProduct.serial_numbers.length === 0) && (
                    <p className="text-gray-500 text-center py-4">No serial numbers</p>
                  )}
                </div>
              </div>
            </div>

            {/* Description */}
            {selectedProduct.description && (
              <div className="mt-4 bg-yellow-50 p-4 rounded-lg">
                <h3 className="font-semibold text-yellow-800 mb-2">📝 Description</h3>
                <p className="text-sm text-gray-700">{selectedProduct.description}</p>
              </div>
            )}

            {/* Specifications */}
            {selectedProduct.specifications && (
              <div className="mt-4 bg-cyan-50 p-4 rounded-lg">
                <h3 className="font-semibold text-cyan-800 mb-2">⚙️ Specifications</h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedProduct.specifications}</p>
              </div>
            )}

            {/* Attachments / Pictures Section - Hidden for data_entry role */}
            {user.role !== 'data_entry' && (
              <div className="mt-4 bg-yellow-50 p-4 rounded-lg" data-testid="product-attachments-section">
                <h3 className="font-semibold text-yellow-800 mb-2">📎 Attachments</h3>
                <input
                  type="file"
                  className="text-sm"
                  accept="image/*,.pdf,.doc,.docx"
                  data-testid="product-attachment-upload"
                />
                <p className="text-xs text-gray-500 mt-1">Upload photos, documents, or maintenance reports</p>
              </div>
            )}

            <div className="mt-6 flex justify-between">
              {hasPermission(user, 'products', 'edit') && (
                <button
                  onClick={() => {
                    setShowDetails(false);
                    openBulkAssignModal(selectedProduct);
                  }}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  data-testid="assign-serials-to-customer-btn"
                >
                  Assign Serials to Customer
                </button>
              )}
              <button
                onClick={() => setShowDetails(false)}
                className="px-6 py-2 bg-gradient-to-r from-blue-700 to-green-700 text-white rounded-lg hover:shadow-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Serial Number Modal */}
      {showSerialModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full m-4">
            <h2 className="text-2xl font-bold mb-6">Add Serial Number</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Serial Number *</label>
                <input
                  type="text"
                  value={serialFormData.serial_number}
                  onChange={(e) => setSerialFormData({...serialFormData, serial_number: e.target.value})}
                  placeholder="SN-12345"
                  className="w-full border rounded px-3 py-2"
                  autoFocus
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Purchase Date *</label>
                <DateInputDMY value={serialFormData.purchase_date}
 onChange={(e) => setSerialFormData({...serialFormData, purchase_date: e.target.value})}
 className="w-full border rounded px-3 py-2"
 />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Supplier Warranty Period (months) *</label>
                <input
                  type="number"
                  value={serialFormData.supplier_warranty_period}
                  onChange={(e) => setSerialFormData({...serialFormData, supplier_warranty_period: e.target.value})}
                  placeholder="e.g., 12"
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              {serialFormData.purchase_date && serialFormData.supplier_warranty_period && (
                <div className="bg-orange-50 p-3 rounded">
                  <p className="text-sm text-orange-800">
                    <strong>Supplier Warranty Expires:</strong> {
                      formatDateDMY(new Date(new Date(serialFormData.purchase_date).setMonth(
                        new Date(serialFormData.purchase_date).getMonth() + parseInt(serialFormData.supplier_warranty_period)
                      )))
                    }
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowSerialModal(false)}
                className="px-6 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={addSerialNumber}
                className="px-6 py-2 bg-gradient-to-r from-blue-700 to-green-700 text-white rounded hover:shadow-lg"
              >
                Add Serial
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Assign to Customer Modal */}
      {showBulkAssignModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-3xl w-full m-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">Assign Serial Numbers to Customer</h2>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">
                Product: <span className="font-bold">{selectedProduct.name}</span>
              </p>
              
              {/* Serial Numbers Selection */}
              <div className="border rounded-lg p-4 mb-4 bg-gray-50">
                <h3 className="font-semibold mb-3">Select Serial Numbers (In Stock Only)</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {selectedProduct.serial_numbers?.map((serial, index) => (
                    serial.status === 'in_stock' && (
                      <div key={serial.serial_number} className="flex items-center gap-3 p-2 bg-white rounded border">
                        <input
                          type="checkbox"
                          checked={selectedSerialIndexes.includes(index)}
                          onChange={() => toggleSerialSelection(index)}
                          className="w-5 h-5"
                        />
                        <span className="font-mono font-medium flex-1">{serial.serial_number}</span>
                        <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">In Stock</span>
                      </div>
                    )
                  ))}
                  {selectedProduct.serial_numbers?.filter(s => s.status === 'in_stock').length === 0 && (
                    <p className="text-gray-500 text-center py-4">No serial numbers available for assignment</p>
                  )}
                </div>
                {selectedSerialIndexes.length > 0 && (
                  <p className="mt-3 text-sm text-blue-600 font-medium">
                    {selectedSerialIndexes.length} serial number(s) selected
                  </p>
                )}
              </div>
            </div>

            {/* Customer Assignment Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Customer *</label>
                <SearchableSelect
                  value={bulkAssignData.customer_id}
                  onChange={(val) => setBulkAssignData({...bulkAssignData, customer_id: val})}
                  options={customers.map(customer => ({ value: customer.id, label: customer.name }))}
                  placeholder="Select Customer"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Sales Date *</label>
                <DateInputDMY value={bulkAssignData.sale_date}
 onChange={(e) => setBulkAssignData({...bulkAssignData, sale_date: e.target.value})}
 className="w-full border rounded px-3 py-2"
 />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Customer Warranty Period (months) *</label>
                <input
                  type="number"
                  value={bulkAssignData.customer_warranty_period}
                  onChange={(e) => setBulkAssignData({...bulkAssignData, customer_warranty_period: e.target.value})}
                  placeholder="Default: 12 months"
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              {bulkAssignData.sale_date && bulkAssignData.customer_warranty_period && (
                <div>
                  <label className="block text-sm font-medium mb-1 text-blue-700">Customer Warranty End Date (Auto-calculated)</label>
                  <input
                    type="text"
                    value={formatDateDMY(new Date(new Date(bulkAssignData.sale_date).setMonth(
                      new Date(bulkAssignData.sale_date).getMonth() + parseInt(bulkAssignData.customer_warranty_period || 0)
                    )))}
                    disabled
                    className="w-full border rounded px-3 py-2 bg-blue-50 font-semibold text-blue-800"
                  />
                  <p className="text-xs text-blue-600 mt-1">
                    Formula: Sales Date ({formatDateDMY(bulkAssignData.sale_date)}) + Warranty Period ({bulkAssignData.customer_warranty_period} months)
                  </p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-1">
                  Next Maintenance Due Date
                  {bulkAssignData.next_maintenance_date && (
                    <span className="ml-2 text-xs text-green-600">(Fetched from Activities)</span>
                  )}
                </label>
                <DateInputDMY value={bulkAssignData.next_maintenance_date}
 onChange={(e) => setBulkAssignData({...bulkAssignData, next_maintenance_date: e.target.value})}
 className="w-full border rounded px-3 py-2"
 />
                <p className="text-xs text-gray-500 mt-1">
                  This date is automatically fetched from the latest activity record for this product
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">License Code</label>
                <input
                  type="text"
                  value={bulkAssignData.license_code}
                  onChange={(e) => setBulkAssignData({...bulkAssignData, license_code: e.target.value})}
                  placeholder="e.g., LIC-2024-XYZ"
                  className="w-full border rounded px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Optional license or activation code for this serial number
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowBulkAssignModal(false);
                  setSelectedSerialIndexes([]);
                  setBulkAssignData({
                    customer_id: '',
                    sale_date: '',
                    customer_warranty_period: '12',
                    next_maintenance_date: ''
                  });
                }}
                className="px-6 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={bulkAssignToCustomer}
                disabled={selectedSerialIndexes.length === 0}
                className={`px-6 py-2 rounded ${
                  selectedSerialIndexes.length === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-700 to-green-700 text-white hover:shadow-lg'
                }`}
              >
                Assign {selectedSerialIndexes.length > 0 ? `${selectedSerialIndexes.length} Serial(s)` : 'Serial Numbers'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Excel Modal */}
      {showImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full m-4">
            <h2 className="text-2xl font-bold mb-4">Import Products from Excel</h2>
            <form onSubmit={handleImport} className="space-y-4">
              <div>
                <label className="block text-sm mb-2">Select Excel File (.xlsx)</label>
                <input
                  type="file"
                  accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => setCsvFile(e.target.files[0])}
                  className="w-full border rounded px-3 py-2"
                  data-testid="products-import-file-input"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Columns supported (case-insensitive, first row = headers):
                  <br />
                  <b>name</b> (required), part_number, category, sub_category, brand, division, model,
                  description, specifications, license_code, price, supplier_warranty_period (months),
                  purchase_date, installation_date.
                  <br />
                  Tip: <b>Export current products to Excel</b> first, then edit and re-import for the easiest format.
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setShowImport(false)} className="px-4 py-2 border rounded">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700" data-testid="products-import-submit">Import</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Inline Assign Serial to Customer Modal */}
      {showInlineAssign && inlineAssignIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="inline-assign-modal">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-1">Assign Serial to Customer</h2>
            <p className="text-sm text-gray-600 mb-4">
              {formData.serial_numbers[inlineAssignIndex]?.serial_number}
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer *</label>
                <SearchableSelect
                  value={inlineAssignData.customer_id}
                  onChange={(val) => setInlineAssignData({ ...inlineAssignData, customer_id: val })}
                  options={customers.map((c) => ({ value: c.id, label: c.name }))}
                  placeholder="Select Customer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sale Date *</label>
                <DateInputDMY value={inlineAssignData.sale_date}
 onChange={(e) => setInlineAssignData({ ...inlineAssignData, sale_date: e.target.value })}
 className="w-full border rounded px-3 py-2"
 data-testid="inline-assign-sale-date"
 />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Warranty (months)</label>
                <input
                  type="number"
                  min="0"
                  value={inlineAssignData.customer_warranty_period}
                  onChange={(e) => setInlineAssignData({ ...inlineAssignData, customer_warranty_period: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  data-testid="inline-assign-warranty-months"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Next Maintenance (optional)</label>
                <DateInputDMY value={inlineAssignData.next_maintenance_date}
 onChange={(e) => setInlineAssignData({ ...inlineAssignData, next_maintenance_date: e.target.value })}
 className="w-full border rounded px-3 py-2"
 />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">License Code (optional)</label>
                <input
                  type="text"
                  value={inlineAssignData.license_code}
                  onChange={(e) => setInlineAssignData({ ...inlineAssignData, license_code: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => { setShowInlineAssign(false); setInlineAssignIndex(null); }}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                data-testid="inline-assign-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyInlineAssign}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                data-testid="inline-assign-confirm-btn"
              >
                Assign
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Note: changes are saved to the product only after clicking <strong>Update Product</strong>.
            </p>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
